import React from "react";
import { Icon, Popup } from "semantic-ui-react";
import { capitalize } from "lodash";

import { selectColumn } from "components/BaseTableV3/selectColumn";
import TagList from "components/TagList";
import { TableLink } from "components/BaseTableV3/TableLink";
import { formatDate } from "components/helpers";
import AudioElement from "components/AudioElement";
import { CallsTableActions } from "components/CallsTableV3/CallsTableActions";
import { CALLS_TABLE_CONSTANTS } from "components/CallsTableV3/CallsTableV3";
import { fetchInternalLines, fetchNumbers } from "hooks/useCallsTable";
import callConstants from "constants/Call";
import CallDispositionService from "services/CallDispositions";
import DialSessionService from "services/DialSessions";
import ContactService from "services/Contact";

export const getCallsTableColumns = ({ callStatuses, invalidateData }) => {
  return [
    selectColumn,
    {
      header: "ID",
      id: "ID",
      accessorKey: "id",
      size: 60,
    },
    {
      header: "Tags",
      id: "Tags",
      accessorKey: "tags",
      enableSorting: false,
      cell: ({ row }) => (
        <TagList
          tags={row.getValue("Tags")}
          modelType={CALLS_TABLE_CONSTANTS.className}
          modelId={row.original.id}
          onUpdate={invalidateData}
          tableCell
        />
      ),
    },
    {
      header: "Entity",
      id: "Entity",
      accessorKey: "entity_name",
      cell: ({ row }) => (
        <TableLink href={`/entities/${row.original.entity_id}`}>
          {row.getValue("Entity")}
        </TableLink>
      ),
    },

    {
      header: "Assignee",
      id: "Assignee",
      accessorKey: "assignee_email",
    },

    {
      header: "Contact ID",
      id: "Contact ID",
      accessorKey: "contact_id",
      cell: ({ row }) => (
        <TableLink href={`/contacts/${row.original.contact_id}`}>
          {row.original.contact_id}
        </TableLink>
      ),
    },
    {
      header: "Contact Name",
      id: "Contact Name",
      accessorKey: "contact_full_name",
      cell: ({ row }) =>
        row.original.contact_full_name ? (
          <TableLink href={`/contacts/${row.original.contact_id}`}>
            {row.original.contact_full_name}
          </TableLink>
        ) : (
          "Unlinked Contact"
        ),
    },
    {
      header: "Contact Number",
      id: "Contact Number",
      accessorKey: "external_number",
      enableSorting: false,
      cell: ({ row }) => {
        const contactId = row.original.contact_id;
        return contactId ? (
          <TableLink href={`/contacts/${contactId}`}>
            {row.original.external_number}
          </TableLink>
        ) : (
          row.original.external_number
        );
      },
    },
    {
      header: "Call Date and Time",
      id: "Call Date and Time",
      accessorKey: "created_at",
      cell: ({ row }) => formatDate(row.original.created_at),
    },
    {
      header: "Disposition",
      id: "Disposition",
      accessorKey: "disposition_name",
      cell: ({ row }) => {
        const { parent_disposition_name, other_comment } =
          row.original?.info || {};
        const { status } = row.original;
        const disposition = row.original.disposition_name;
        if (disposition) {
          return disposition;
        } else if (other_comment) {
          return parent_disposition_name
            ? `Other (${parent_disposition_name}): ${other_comment}`
            : `Other: ${other_comment}`;
        } else if (status) {
          return (
            <Popup
              content="No disposition set. Twilio Status is displayed"
              trigger={<span style={{ fontStyle: "italic" }}>[{status}]</span>}
            />
          );
        }
        return null;
      },
    },
    {
      header: "Agent Hung Up",
      id: "Agent Hung Up",
      accessorKey: "agent_hungup",
      cell: ({ row }) => (
        <div style={{ minWidth: "25px" }}>
          <Icon
            name={row.original.agent_hungup ? "check" : "close"}
            color={row.original.agent_hungup ? "green" : "red"}
          />
        </div>
      ),
    },
    {
      header: "Verified",
      id: "Verified",
      accessorKey: "status_verified",
      cell: ({ row }) => (
        <div style={{ minWidth: "25px" }}>
          <Icon
            name={row.original.status_verified ? "check" : "close"}
            color={row.original.status_verified ? "green" : "red"}
          />
        </div>
      ),
    },
    {
      header: "Recording",
      id: "Recording",
      accessorKey: "recording.url",
      enableSorting: false,
      cell: ({ row }) => {
        const value = row.original?.recording?.url;
        const info = row.original?.info;
        const recordingUrl = value || info?.recording_info?.recording_url;
        // Twilio measures the audio itself. A manually logged call has no
        // Twilio metadata, so it falls back to the call leg, which runs
        // slightly longer than the recording.
        const durationSeconds =
          info?.recording_info?.recording_duration || row.original.duration;
        return recordingUrl ? (
          <AudioElement
            src={recordingUrl}
            id={`audio-el-${row.original.id}`}
            durationSeconds={durationSeconds}
          />
        ) : null;
      },
    },
    {
      header: "Agent",
      id: "Agent",
      accessorKey: "agent_full_name",
    },
    {
      header: "Duration",
      id: "Duration",
      accessorKey: "duration",
    },
    {
      header: "Internal Line",
      id: "Internal Line",
      accessorKey: "internal_number",
    },
    {
      header: "Dial Session Name",
      id: "Dial Session Name",
      accessorKey: "dial_session_name",
    },
    {
      header: "Call Direction",
      id: "Call Direction",
      accessorKey: "call_direction",
      cell: ({ row }) => capitalize(row.original.call_direction),
    },
    {
      header: "Manual",
      id: "Manual",
      accessorKey: "is_manual",
      cell: ({ row }) => {
        return <div>{row.original.is_manual && <Icon name="check" />}</div>;
      },
    },
    {
      header: <div></div>,
      id: "Actions",
      accessorKey: "actions",
      enableSorting: false,
      enableResizing: false,
      size: 60,
      meta: {
        isDisplay: true,
      },
      cell: ({ row }) => (
        <CallsTableActions
          original={row.original}
          callStatuses={callStatuses}
          invalidateData={invalidateData}
        />
      ),
    },
  ];
};

export const getCallsTableFilters = ({ tags, assignees, campaignId }) => {
  const callDirectionOptions = callConstants.callDirections.map(cd => ({
    id: cd,
    name: capitalize(cd),
  }));
  return [
    {
      key: "call_datetime",
      title: "Call Date and Time",
      type: "dateRange",
      includeTime: true,
    },
    {
      key: "dial_session_id",
      title: "Dial Session",
      type: "ruvixxSelect",
      queryFn: DialSessionService.getDialSessionsForFilters,
      props: {
        campaign_ids: campaignId ? [campaignId] : [],
      },
    },
    {
      key: "contact_id",
      title: "Contact",
      type: "ruvixxSelect",
      queryFn: ContactService.getContactsForFilters,
      props: {
        campaign_ids: campaignId ? [campaignId] : [],
      },
    },
    {
      key: "agent_id",
      title: "Agent",
      type: "select",
      data: assignees,
    },
    {
      key: "contact_number",
      title: "Contact Number",
      type: "ruvixxSelect",
      queryFn: fetchNumbers,
    },
    {
      key: "caller_id",
      title: "Internal Line",
      type: "ruvixxSelect",
      queryFn: fetchInternalLines,
    },
    {
      key: "disposition_id",
      title: "Call Disposition",
      type: "ruvixxSelect",
      queryFn: async filters => {
        if (filters["model_id"]?.includes("Null")) {
          filters["model_id"] = filters["model_id"].filter(id => id !== "Null");
          if (!filters["model_id"].length) {
            delete filters["model_id"];
          }
        }
        return await CallDispositionService.getDispositionsForFilters(filters);
      },
      props: {
        includeNoneOption: true,
      },
    },
    {
      key: "status_verified",
      title: "Status Verified",
      type: "select",
      data: [
        { id: "verified", name: "Verified" },
        { id: "unverified", name: "Not Verified" },
      ],
    },
    {
      key: "duration",
      title: "Duration",
      type: "range",
    },
    {
      key: "tag_id",
      title: "Tag",
      type: "tag",
      data: tags,
    },
    {
      key: "recording",
      title: "Recording",
      type: "select",
      data: [
        { id: "yes", name: "Has" },
        { id: "no", name: "Doesn't have" },
      ],
      isSingle: true,
      clearable: true,
    },
    {
      key: "transcript",
      title: "Transcript",
      type: "select",
      data: [
        { id: "yes", name: "Has Transcript" },
        { id: "no", name: "No Transcript" },
      ],
      isSingle: true,
      clearable: true,
    },
    {
      key: "agent_hungup",
      title: "Agent Hung Up",
      type: "ruvixxToggle",
    },
    {
      key: "call_direction",
      title: "Call Direction",
      type: "select",
      data: callDirectionOptions,
      isSingle: true,
      clearable: true,
    },
    {
      title: "Is Manual",
      key: "grid_1",
      type: "grid",
      components: [
        { key: "manual", title: "Manual" },
        { key: "not_manual", title: "Not Manual" },
      ],
    },
  ];
};
